import toDuration from './duration';

const numericGroupOpts = (type) => {
  switch (type) {
    case 'milhar':
      return { divisor: 1000, suffix: 'm' };
    case 'milhao':
      return { divisor: 1000000, suffix: 'mi' };
    case 'bilhao':
      return { divisor: 1000000000, suffix: 'bi' };
    default:
      return { divisor: 1, suffix: '' };
  }
};

const localeAndCurrencyOpts = (currency) => {
  switch (currency) {
    case 'R$':
      return { style: 'currency', locale: 'pt-BR', currency: 'BRL' };
    case 'US$':
      return { style: 'currency', locale: 'en-US', currency: 'USD' };
    case '€':
      return { style: 'currency', locale: 'de-DE', currency: 'EUR' };
    default:
      return { style: 'decimal', locale: 'pt-BR' };
  }
};

const toLocaleValue = (value, options = {}) => (
  parseFloat(value, 10).toLocaleString(options.locale, options)
);

/**
 * Format the value numbers.
 * @param {number|string} value value of a portion.
 * @param {string} currency Formats the value for the given currency.
 * Also determine the location.
 * @param {string} fDecimals How to handle the fraction digits.
 * @param {string} nDecimals Number of fraction digits if `fDecimals` is 'fixed'.
 * @param {string} formatType Numeric style.
 * @param {string} grouping Numeric grouping style.
 * @returns Formatted value.
 */
export const dataValueFormat = (
  value, currency, fDecimals, nDecimals, formatType, subFormatType, grouping, fDuration,
) => {
  if (!Number.isNaN(value)) {
    const localeOpts = localeAndCurrencyOpts(currency);

    if (fDecimals) {
      if (fDecimals === 'fixed') {
        localeOpts.minimumFractionDigits = nDecimals || 0;
        localeOpts.maximumFractionDigits = nDecimals || 0;
      }
    } else localeOpts.minimumFractionDigits = nDecimals || 0;

    if (subFormatType === 'variation') localeOpts.signDisplay = 'exceptZero';

    if (['percentual', 'both-flip'].includes(formatType)) {
      localeOpts.style = 'percent';
      return `${toLocaleValue(value / 100, localeOpts)}`;
    }

    if (fDuration) return toDuration(value, fDuration);

    const groupingTools = numericGroupOpts(grouping);
    return `${toLocaleValue(value / groupingTools.divisor, localeOpts)}${groupingTools.suffix}`;
  } return null;
};

export const labelValues = (value, value2, config, category = '') => {
  const val1 = dataValueFormat(
    Number(value),
    config?.LabelCurrencyControl,
    config?.Decimals,
    config?.separadorNumericoCasas,
    config?.DataFormat,
    config?.DataFormat === 'percentual' ? config?.DataPercentFormat : config?.DataAbsoluteFormat,
    config?.type === 'Value' ? config?.GroupingSelectControl : config?.LabelGroupingControl,
    config?.DataAbsoluteFormat === 'duration' ? (config?.DurationUnit ?? 'hours') : null,
  );
  const val2 = config?.DataFormatAlt !== 'none' && value2 !== null ? `(${dataValueFormat(
    Number(value2),
    config?.LabelCurrencyControl,
    config?.Decimals,
    config?.separadorNumericoCasas,
    config?.DataFormatAlt,
    config?.DataFormatAlt === 'percentual' ? config?.DataPercentFormatAlt : config?.DataAbsoluteFormatAlt,
    config?.type === 'Value' ? config?.GroupingSelectControl : config?.LabelGroupingControl,
    config?.DataAbsoluteFormatAlt === 'duration' ? (config?.DurationUnit ?? 'hours') : null,
  )})` : '';
  const prefix = config?.PrefixControl?.trim() || '';
  const suffix = config?.SufixControl?.trim() || '';

  switch (config?.LabelAxisSelect) {
    case 'x': {
      return [`${prefix} ${category}`.trim(), suffix.trim()];
    }
    case 'x : y': {
      return [`${prefix} ${category} : ${val1}`.trim(), `${val2} ${suffix}`.trim()];
    }
    case 'y : x': {
      return [`${prefix} ${val1}`.trim(), `${val2} : ${category} ${suffix}`.trim()];
    }
    default: {
      return [`${prefix} ${val1}`.trim(), `${val2} ${suffix}`.trim()];
    }
  }
};

export const labelValuesStr = (value, value2, config, category = '') => labelValues(value, value2, config, category).join(' ').trim();

export const tooltipValues = (value, value2, config) => {
  const val1 = dataValueFormat(
    Number(value),
    config?.InteractiveCurrencyControl,
    config?.InteractiveDecimals,
    config?.InteractiveSeparadorNumericoCasas,
    config?.DataFormat,
    config?.DataFormat === 'percentual' ? config?.DataPercentFormat : config?.DataAbsoluteFormat,
    config?.InteractiveGroupingControl,
    config?.DataAbsoluteFormat === 'duration' ? (config?.DurationUnit ?? 'hours') : null,
  );
  const val2 = config?.DataFormatAlt !== 'none' && value2 !== null ? `(${dataValueFormat(
    Number(value2),
    config?.InteractiveCurrencyControl,
    config?.InteractiveDecimals,
    config?.InteractiveSeparadorNumericoCasas,
    config?.DataFormatAlt,
    config?.DataFormatAlt === 'percentual' ? config?.DataPercentFormatAlt : config?.DataAbsoluteFormatAlt,
    config?.InteractiveGroupingControl,
    config?.DataAbsoluteFormatAlt === 'duration' ? (config?.DurationUnit ?? 'hours') : null,
  )})` : '';
  const prefix = config?.InteractivePrefixControl?.trim() || '';
  const suffix = config?.InteractiveSufixControl?.trim() || '';
  return [`${prefix} ${val1}`.trim(), `${val2} ${suffix}`.trim()];
};

export const tooltipValuesStr = (value, value2, config) => tooltipValues(value, value2, config).join(' ').trim();

/**
 * Format the `axis values` in the chart and add prefix and/or suffix (visual only).
 * @param {number} value value of a portion.
 * @param {object} config Style config.
 * @returns Formatted value.
 */
export const customValueAxis = (value, config, axisType = 'X') => {
  const configType = config?.[`EnableAxis${axisType}`];
  const fValue = dataValueFormat(
    Number(value),
    config?.CurrencyControl,
    configType?.Decimals,
    configType?.[`separadorNumericoCasas${axisType}`],
    config?.DataFormat,
    config?.DataFormat === 'percentual' ? config?.DataPercentFormat : config?.DataAbsoluteFormat,
    config?.GroupingSelectControl,
    config?.DataAbsoluteFormat === 'duration' ? (config?.DurationUnit ?? 'hours') : null,
  );
  return fValue || value;
};

export const staticPercent = (value, fDecimals = 'auto', nDecimals = 0) => {
  const options = {
    style: 'percent',
  };
  if (fDecimals) {
    if (fDecimals === 'fixed') {
      options.maximumFractionDigits = nDecimals;
      options.minimumFractionDigits = nDecimals;
    }
  } else options.minimumFractionDigits = nDecimals;
  return toLocaleValue(value, options);
};
